/**
 * OrgTheme → CSS custom property mapping (ARCHITECTURE §7 `theme/`).
 *
 * The single place that translates an {@link OrgTheme} into the `--lc-*` CSS
 * variables that Tailwind (in both apps) references. Because components read the
 * vars — never the theme object — org branding applies with zero component
 * changes: swap the vars, everything rebrands.
 */

import type { OrgTheme } from '../types';

/** The canonical `--lc-*` variable names. Keep in sync with both apps' Tailwind config. */
export const LC_CSS_VARS = {
  primary: '--lc-primary',
  onPrimary: '--lc-on-primary',
  background: '--lc-bg',
  text: '--lc-text',
  font: '--lc-font',
  radius: '--lc-radius',
  // Widget header (advanced) — the gradient var equals `header` when no 2nd stop is set,
  // so `linear-gradient(var(--lc-header), var(--lc-header-gradient))` is flat by default.
  header: '--lc-header',
  onHeader: '--lc-on-header',
  headerGradient: '--lc-header-gradient',
  // Chat window (advanced) — the widget's existing WhatsApp-style surfaces, now theme-driven.
  bubbleOut: '--lc-bubble-out', // visitor's own (outgoing) bubble
  bubbleIn: '--lc-bubble-in', //  agent (incoming) bubble
  chatBg: '--lc-chat-bg', //      message-list wallpaper
  // Chat wallpaper gradient (v2) — 2nd stop; == chatBg when flat so gradients render flat by default.
  chatBgGradient: '--lc-chat-bg-gradient',
  // Bubble text (v2) — per-side foreground; fall back to base text so unset orgs are readable.
  bubbleOutText: '--lc-bubble-out-text',
  bubbleInText: '--lc-bubble-in-text',
  // Message typography (v2).
  msgFontSize: '--lc-msg-font-size',
  // Buttons (v2) — send + bot option buttons; default to primary. Own font so buttons can differ.
  sendBtn: '--lc-send-btn',
  botBtn: '--lc-bot-btn',
  btnFont: '--lc-btn-font',
  btnFontSize: '--lc-btn-font-size',
  // Avatars (v2) — background behind a preset glyph; default to primary / bubble-in.
  agentAvatarBg: '--lc-agent-avatar-bg',
  userAvatarBg: '--lc-user-avatar-bg',
} as const;

/** WhatsApp-style fallbacks for the chat surfaces (mirror `widget.css` :root defaults). */
const DEFAULT_BUBBLE_OUT = '#d9fdd3'; // visitor green
const DEFAULT_BUBBLE_IN = '#ffffff'; //  agent white
const DEFAULT_CHAT_BG = '#efe7de'; //    wallpaper beige

export type LcCssVarName = (typeof LC_CSS_VARS)[keyof typeof LC_CSS_VARS];

/** A map of CSS variable name → value, ready to write onto an element's style. */
export type ThemeVarMap = Record<LcCssVarName, string>;

/** Default brand theme — WhatsApp-style green so the widget ships WhatsApp-themed out of the box. */
export const DEFAULT_THEME: OrgTheme = {
  primaryColor: '#00A884', // WhatsApp teal-green (launcher / buttons / agent header)
  onPrimaryColor: '#ffffff',
  backgroundColor: '#ffffff',
  textColor: '#111B21',
  fontFamily:
    "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  radius: 12,
  launcherPosition: 'bottom-right',
};

/**
 * Convert an {@link OrgTheme} to its CSS variable map. Pure — no DOM access
 * (see `applyTheme.ts` for the side-effecting writer). Missing fields fall back
 * to {@link DEFAULT_THEME}.
 */
export function themeToCssVars(theme: Partial<OrgTheme> | undefined): ThemeVarMap {
  const t: OrgTheme = { ...DEFAULT_THEME, ...theme };
  // Advanced fields fall back to the base palette so an org that only sets `primaryColor`
  // still gets a coherent header + agent bubble (never a stale baked-in default).
  const header = t.headerColor || t.primaryColor;
  const chatBg = t.chatBackgroundColor || DEFAULT_CHAT_BG;
  const bubbleOut = t.visitorBubbleColor || DEFAULT_BUBBLE_OUT;
  const bubbleIn = t.agentBubbleColor || DEFAULT_BUBBLE_IN;
  return {
    [LC_CSS_VARS.primary]: t.primaryColor,
    [LC_CSS_VARS.onPrimary]: t.onPrimaryColor,
    [LC_CSS_VARS.background]: t.backgroundColor,
    [LC_CSS_VARS.text]: t.textColor,
    [LC_CSS_VARS.font]: t.fontFamily,
    [LC_CSS_VARS.radius]: `${t.radius}px`,
    [LC_CSS_VARS.header]: header,
    [LC_CSS_VARS.onHeader]: t.headerTextColor || t.onPrimaryColor,
    // No 2nd stop → gradient var == header, so the gradient renders flat.
    [LC_CSS_VARS.headerGradient]: t.headerGradientColor || header,
    [LC_CSS_VARS.bubbleOut]: bubbleOut,
    [LC_CSS_VARS.bubbleIn]: bubbleIn,
    [LC_CSS_VARS.chatBg]: chatBg,
    // v2 — chat wallpaper gradient: only a 2nd stop when explicitly a gradient, else flat.
    [LC_CSS_VARS.chatBgGradient]:
      t.backgroundMode === 'gradient' && t.backgroundGradientColor
        ? t.backgroundGradientColor
        : chatBg,
    // v2 — bubble text: per-side foreground, fall back to base text.
    [LC_CSS_VARS.bubbleOutText]: t.userTextColor || t.textColor,
    [LC_CSS_VARS.bubbleInText]: t.agentTextColor || t.textColor,
    // v2 — message typography.
    [LC_CSS_VARS.msgFontSize]: `${t.fontSize || 14}px`,
    // v2 — buttons default to the brand primary; own font can diverge from body.
    [LC_CSS_VARS.sendBtn]: t.sendButtonColor || t.primaryColor,
    [LC_CSS_VARS.botBtn]: t.botButtonColor || t.primaryColor,
    [LC_CSS_VARS.btnFont]: t.buttonFontFamily || t.fontFamily,
    [LC_CSS_VARS.btnFontSize]: `${t.buttonFontSize || 14}px`,
    // v2 — avatar backgrounds behind preset glyphs.
    [LC_CSS_VARS.agentAvatarBg]: t.agentIconBg || t.primaryColor,
    [LC_CSS_VARS.userAvatarBg]: t.userIconBg || bubbleIn,
  };
}
